import type { IterationError, IterationOutcomes, SpotError } from "@/generated";

/** Output key under which a fan-out that failed under the `fail` policy
 *  records which items failed and which never started (flow_v2/hooks.py). */
export const ITERATION_OUTCOMES_KEY = "_iterations";

/** Output keys the engine writes for bookkeeping, not node data. */
export function isBookkeepingOutputKey(key: string): boolean {
  return key === "_error" || key === ITERATION_OUTCOMES_KEY;
}

function isIterationList(value: unknown): value is number[] {
  return (
    Array.isArray(value) &&
    value.every((it): it is number => typeof it === "number")
  );
}

function isIterationError(value: unknown): value is IterationError {
  if (!value || typeof value !== "object") return false;
  const { iteration, error } = value as {
    iteration?: unknown;
    error?: unknown;
  };
  return typeof iteration === "number" && !!error && typeof error === "object";
}

export function readIterationOutcomes(
  container: unknown
): IterationOutcomes | null {
  if (!container || typeof container !== "object") return null;
  const value = (container as { value?: unknown }).value;
  if (!value || typeof value !== "object") return null;
  const {
    failed,
    not_run,
    skipped = [],
  } = value as { failed?: unknown; not_run?: unknown; skipped?: unknown };
  if (!Array.isArray(failed) || !failed.every(isIterationError)) return null;
  if (!isIterationList(not_run) || !isIterationList(skipped)) return null;
  return { failed, not_run, skipped };
}

export type IterationPage =
  | { kind: "ok" }
  | { kind: "failed"; error: SpotError }
  | { kind: "not_run" };

/** What iteration `page` (1-based) of a fan-out should show. */
export function iterationPage(
  outcomes: IterationOutcomes | null | undefined,
  page: number
): IterationPage {
  if (!outcomes) return { kind: "ok" };
  const failed = outcomes.failed.find((item) => item.iteration === page);
  if (failed) return { kind: "failed", error: failed.error };
  if (outcomes.not_run.includes(page)) return { kind: "not_run" };
  return { kind: "ok" };
}

export function failedIterations(
  outcomes: IterationOutcomes | null | undefined
): number[] {
  if (!outcomes) return [];
  return [...new Set(outcomes.failed.map((item) => item.iteration))].sort(
    (a, b) => a - b
  );
}

export function firstFailedIteration(
  outcomes: IterationOutcomes | null | undefined
): number | null {
  return failedIterations(outcomes)[0] ?? null;
}

export function iterationCounts(
  totalIterations: number,
  outcomes: IterationOutcomes
): { succeeded: number; failed: number; skipped: number; notRun: number } {
  const failed = failedIterations(outcomes).length;
  const skipped = new Set(outcomes.skipped).size;
  const notRun = new Set(outcomes.not_run).size;
  return {
    succeeded: Math.max(0, totalIterations - failed - skipped - notRun),
    failed,
    skipped,
    notRun,
  };
}

/** V2 stores one consolidated IO record whose arrays hold one slot per
 * fan-out iteration — paged by slicing client-side, never by refetching.
 * Returns the page count: `total_it` when there is an array to slice, else 1.
 * The endpoint caps list values at read time (MAX_LIST_ITEMS_PER_OUTPUT), so
 * pages are additionally capped at the longest array actually received —
 * slots beyond it only exist in the downloadable full output.
 * A fan-out that failed records per-item `outcomes`; every iteration is then
 * a page (its own error, "not run", or the finished item's outputs, fetched
 * window by window) even when no data array survived — e.g. the first item
 * failed and nothing else ran.
 */
export function v2FanOutPageCount(
  totalIt: number | undefined,
  values: unknown[],
  outcomes?: IterationOutcomes | null
): number {
  const total = totalIt ?? 0;
  if (total <= 1) return 1;
  if (outcomes) return total;
  const longest = values.reduce<number>(
    (max, value) => (Array.isArray(value) ? Math.max(max, value.length) : max),
    0
  );
  return longest > 0 ? Math.min(total, longest) : 1;
}
